'use client';

import React from 'react';
import { Card, CardContent } from '@/shared/ui/shadcn/card';
import { Progress } from '@/shared/ui/shadcn/progress';

interface AlbumProgressIndicatorProps {
  isGeneratingTexts: boolean;
  isGeneratingUpscales: boolean;
  textsProgress: number;
  upscalesProgress: number;
  className?: string;
}

export function AlbumProgressIndicator({
  isGeneratingTexts,
  isGeneratingUpscales,
  textsProgress,
  upscalesProgress,
  className = '',
}: AlbumProgressIndicatorProps) {
  if (!isGeneratingTexts && !isGeneratingUpscales) {
    return null;
  }

  const currentStep = isGeneratingTexts ? 'texts' : 'upscales';
  const currentProgress = isGeneratingTexts ? textsProgress : upscalesProgress;
  const isStalling = currentProgress >= 95;
  const stepLabel = isGeneratingTexts
    ? 'Generating album texts...'
    : 'Upscaling images...';
  const stepDescription = isGeneratingTexts
    ? 'Creating professional documentation for your design'
    : 'Enhancing image quality for high-resolution output';

  return (
    <Card className={className}>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-list-item-value text-foreground">{stepLabel}</h3>
            <p className="text-card-meta text-muted-foreground mt-1">
              {stepDescription}
            </p>
          </div>
          <div className="text-list-item-value text-foreground">
            {Math.round(currentProgress)}%
          </div>
        </div>

        <Progress
          value={currentProgress}
          className="h-2"
          isStalling={isStalling}
        />

        {isGeneratingTexts && isGeneratingUpscales && (
          <div className="flex items-center space-x-2 text-card-meta text-muted-foreground">
            <div className="flex items-center">
              <div
                className={`w-2 h-2 rounded-full ${currentStep === 'texts' ? 'bg-primary' : 'bg-muted'} mr-1`}
              />
              <span>Text Generation</span>
            </div>
            <span>→</span>
            <div className="flex items-center">
              <div
                className={`w-2 h-2 rounded-full ${currentStep === 'upscales' ? 'bg-primary' : 'bg-muted'} mr-1`}
              />
              <span>Image Upscaling</span>
            </div>
          </div>
        )}

        <div className="flex items-center text-card-meta text-muted-foreground">
          <svg
            className="animate-spin h-4 w-4 mr-2 text-primary"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>
            {isGeneratingTexts
              ? 'Estimated time: 2 minutes'
              : 'Estimated time: 2 minutes per image'}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
